import React, { useState } from 'react';
import {
  testInstagrapiConnection,
  saveInstagrapiSession,
  submitInstagrapiChallenge,
  resendInstagrapiChallenge,
} from '../api';
import { InstagramChallengeInput } from './InstagramChallengeInput';
import { InstagramRegionField } from './InstagramRegionField';
import { resolveDefaultCountry } from '../constants/countries';
import type { Workspace } from '../types';
import './InstagramConnectInline.css';

type Props = {
  workspace: Workspace;
  /** Called with the connected username after the session is saved. */
  onConnected: (_username: string) => void;
  /** Called when the user chooses to do this later. */
  onSkip?: () => void;
};


/**
 * Self-contained Instagram connect flow for embedding in chat (onboarding).
 * Owns its own username/password/challenge state; the full-featured variant
 * with session management lives on the Settings page.
 */
export function InstagramConnectInline({ workspace, onConnected, onSkip }: Props) {
  const [username, setUsername] = useState(workspace.instagrapi_username || '');
  const [password, setPassword] = useState('');
  // Onboarding is where most accounts are connected, and this used to hardcode
  // 'US' — so every user who never opened the dropdown published from a US IP.
  const [country, setCountry] = useState(
    resolveDefaultCountry(workspace.instagram_account_country)
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(
    Boolean(workspace.has_instagrapi_session)
  );

  const [challengeToken, setChallengeToken] = useState('');
  const [challengeType, setChallengeType] = useState<'email' | 'sms' | 'totp'>('email');
  const [maskedContact, setMaskedContact] = useState('');
  const [challengeError, setChallengeError] = useState<string | undefined>(undefined);
  const [submittingChallenge, setSubmittingChallenge] = useState(false);

  // Correlation id from the connect attempt, echoed back on save so the server
  // log shows one continuous journey instead of two unrelated events.
  const [flowId, setFlowId] = useState<string | undefined>(undefined);

  const finishConnect = async (user: string, sessionData: string) => {
    await saveInstagrapiSession(workspace.id, user, sessionData, country, flowId);
    setConnected(true);
    setPassword('');
    setChallengeToken('');
    onConnected(user);
  };

  const handleConnect = async () => {
    const user = username.trim();
    if (!user || !password) {
      setError('Enter your Instagram username and password.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await testInstagrapiConnection(workspace.id, user, password, country);
      setFlowId(result.flow_id);
      if (result.auth_status === 'challenge_pending' && result.challenge_token) {
        setChallengeToken(result.challenge_token);
        setChallengeType((result.challenge_type as 'email' | 'sms' | 'totp') ?? 'email');
        setMaskedContact(result.masked_contact ?? '');
        setChallengeError(undefined);
        return;
      }
      if (!result.valid || !result.session_data) {
        setError(result.error ?? 'Instagram login failed. Check your credentials.');
        return;
      }
      await finishConnect(result.username ?? user, result.session_data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect Instagram.');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitChallenge = async (code: string) => {
    setSubmittingChallenge(true);
    setChallengeError(undefined);
    try {
      const result = await submitInstagrapiChallenge(workspace.id, challengeToken, code);
      if (!result.valid || !result.session_data) {
        setChallengeError(result.error ?? 'Verification failed. Try again.');
        return;
      }
      await finishConnect(result.username ?? username.trim(), result.session_data);
    } catch (err) {
      setChallengeError(
        err instanceof Error ? err.message : 'Verification failed. Try again.'
      );
    } finally {
      setSubmittingChallenge(false);
    }
  };

  const handleResendChallenge = async () => {
    if (!challengeToken) return { sent: false, error: 'No active challenge.' };
    try {
      const res = await resendInstagrapiChallenge(workspace.id, challengeToken);
      if (!res.sent && res.auth_status === 'challenge_expired') {
        setChallengeToken('');
      }
      return { sent: res.sent, error: res.error };
    } catch (err) {
      return {
        sent: false,
        error: err instanceof Error ? err.message : 'Could not resend the code.',
      };
    }
  };

  if (connected) {
    return (
      <div className="igc-inline igc-inline--done">
        <span className="igc-inline__check">✓</span>
        <span>
          {workspace.instagrapi_username || username
            ? `@${workspace.instagrapi_username || username.trim()} connected`
            : 'Instagram connected'}
        </span>
      </div>
    );
  }

  if (challengeToken) {
    return (
      <div className="igc-inline">
        <div className="igc-inline__title">📸 Verify it&apos;s you</div>
        <InstagramChallengeInput
          challengeType={challengeType}
          maskedContact={maskedContact}
          loading={submittingChallenge}
          error={challengeError}
          onSubmit={handleSubmitChallenge}
          onCancel={() => setChallengeToken('')}
          onResend={challengeType === 'totp' ? undefined : handleResendChallenge}
        />
      </div>
    );
  }

  return (
    <div className="igc-inline">
      <div className="igc-inline__title">📸 Connect Instagram</div>
      <p className="igc-inline__desc">
        We log in once and keep a private session so Conistra can publish for you.
        Have your authenticator app ready — Instagram will ask for a code.
      </p>
      <div className="igc-inline__fields">
        <input
          className="igc-inline__input"
          type="text"
          autoComplete="username"
          placeholder="Instagram username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          disabled={busy}
        />
        <input
          className="igc-inline__input"
          type="password"
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
          disabled={busy}
        />
      </div>
      <div className="igc-inline__region">
        <InstagramRegionField
          value={country}
          onChange={setCountry}
          idPrefix="onboarding-ig-region"
          disabled={busy}
        />
      </div>
      {error && <div className="igc-inline__error">{error}</div>}
      <div className="igc-inline__actions">
        <button
          type="button"
          className="igc-inline__connect"
          onClick={handleConnect}
          disabled={busy}
        >
          {busy ? 'Connecting…' : 'Connect'}
        </button>
        {onSkip && (
          <button
            type="button"
            className="igc-inline__skip"
            onClick={onSkip}
            disabled={busy}
          >
            I&apos;ll do this later
          </button>
        )}
      </div>
    </div>
  );
}
